export interface FormProblemState {
  readonly fieldCount: number
  readonly kind: 'client' | 'failure' | 'network' | 'serverMapped' | 'serverUnmapped'
  readonly messages: readonly string[]
  readonly requestId: string | null
}

/** The shape fromFailure actually reads off a normalized problem -- not the whole model. */
type FailedProblem = Readonly<{
  code: string
  fieldIssues: Readonly<Record<string, readonly unknown[]>>
  requestId: string | null
}>

/** The shape fromFailure actually reads off a field issue -- not the whole generated type. */
type PointerIssue = Readonly<{ source: Readonly<{ pointer: string }> }>

type ProblemResolvers<TProblem extends FailedProblem, TIssue extends PointerIssue> = Readonly<{
  issue: (issue: TIssue) => string
  problem: (problem: TProblem) => string
}>

/** A browser-side rejection; it never reached the server, so it carries no reference. */
const fromClientValidation = (fieldCount: number): FormProblemState => ({
  fieldCount,
  kind: 'client',
  messages: [],
  requestId: null
})

/** A failure stated as one message, carrying the server request id when the server issued one. */
const fromMessage = (message: () => string, requestId: string | null): FormProblemState => ({
  fieldCount: 0,
  kind: 'failure',
  get messages(): readonly string[] {
    return [message()]
  },
  requestId
})

const distinctPointerCount = (issues: readonly PointerIssue[]): number =>
  new Set(issues.map((issue) => issue.source.pointer)).size

const fromFailure = <TProblem extends FailedProblem, TIssue extends PointerIssue>(
  problem: TProblem,
  unmapped: readonly TIssue[],
  resolve: ProblemResolvers<TProblem, TIssue>
): FormProblemState => {
  if (problem.code === 'VALIDATION_ERROR' && unmapped.length === 0) {
    return {
      fieldCount: Object.keys(problem.fieldIssues).length,
      kind: 'serverMapped',
      messages: [],
      requestId: problem.requestId
    }
  }

  if (problem.code === 'VALIDATION_ERROR') {
    return {
      fieldCount: Object.keys(problem.fieldIssues).length - distinctPointerCount(unmapped),
      kind: 'serverUnmapped',
      get messages(): readonly string[] {
        return unmapped.map(resolve.issue)
      },
      requestId: problem.requestId
    }
  }

  if (problem.code === 'INTERNAL_ERROR' && problem.requestId === null) {
    return { fieldCount: 0, kind: 'network', messages: [], requestId: null }
  }

  return {
    fieldCount: 0,
    kind: 'failure',
    get messages(): readonly string[] {
      return [resolve.problem(problem)]
    },
    requestId: problem.requestId
  }
}

/** Kinds whose banner headline is a field count, and so whose banner ends once no field is left in error. */
const FIELD_COUNTED_KINDS: ReadonlySet<FormProblemState['kind']> = new Set([
  'client',
  'serverMapped',
  'serverUnmapped'
])

/** Marks a verdict whose fieldCount is read live; a static count never ends its banner. Survives reactive proxies. */
const LIVE_FIELD_COUNT = Symbol('liveFieldCount')

const markLive = (state: FormProblemState): FormProblemState =>
  Object.defineProperty(state, LIVE_FIELD_COUNT, { value: true })

const isLive = (state: FormProblemState): boolean => LIVE_FIELD_COUNT in state

/** A client verdict always counts live; a server verdict counts live only when it named a control. */
const countsLive = (state: FormProblemState): boolean =>
  state.kind === 'client' || (FIELD_COUNTED_KINDS.has(state.kind) && state.fieldCount > 0)

/** The same verdict with fieldCount (and, when given, messages) read live; a server verdict that named no control stays static. */
const withLiveFieldCount = (
  state: FormProblemState,
  count: () => number,
  liveMessages?: () => readonly string[]
): FormProblemState => {
  if (countsLive(state)) {
    return markLive({
      get fieldCount(): number {
        return count()
      },
      kind: state.kind,
      get messages(): readonly string[] {
        return liveMessages?.() ?? state.messages
      },
      requestId: state.requestId
    })
  }
  return state
}

/** Two independently raised verdicts as one banner; count, messages and kind read their parts live, so combining never tracks a live part. */
const combine = (first: FormProblemState, second: FormProblemState): FormProblemState => {
  const messages = (): readonly string[] => [...first.messages, ...second.messages]
  const combined: FormProblemState = {
    get fieldCount(): number {
      return first.fieldCount + second.fieldCount
    },
    get kind(): FormProblemState['kind'] {
      return messages().length > 0 ? 'failure' : first.kind
    },
    get messages(): readonly string[] {
      return messages()
    },
    requestId: first.requestId ?? second.requestId
  }
  return isLive(first) || isLive(second) ? markLive(combined) : combined
}

/** Whether the verdict still describes something on screen: a live field-count banner lasts only while a field or a listed message is in error. */
const isPresentable = (state: FormProblemState): boolean =>
  state.fieldCount > 0 ||
  state.messages.length > 0 ||
  !FIELD_COUNTED_KINDS.has(state.kind) ||
  !isLive(state)

export const FormProblemState = Object.freeze({
  combine,
  fromClientValidation,
  fromFailure,
  fromMessage,
  isPresentable,
  withLiveFieldCount
})
